<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveLanguage">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Edit language') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            v-if="language.locale && language.locale !== defaultLocale"
                            class="btn btn-red shadow-sm rounded"
                            type="button"
                            @click="deleteLanguageModal = true"
                        >
                            {{ $t('Delete language') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-sm">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-3">
                        <div class="mt-5 md:mt-0 md:col-span-3">
                            <div class="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label class="block text-xs font-medium text-gray-600 mb-1" for="translation-file-filter">
                                        {{ $t('Component/file') }}
                                    </label>
                                    <select
                                        id="translation-file-filter"
                                        v-model="filters.file"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        @change="onFilterChanged"
                                    >
                                        <option value="">{{ $t('All components') }}</option>
                                        <option v-for="file in availableFiles" :key="file.path" :value="file.path">
                                            {{ file.label }} ({{ file.key_count }})
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-xs font-medium text-gray-600 mb-1" for="translation-status-filter">
                                        {{ $t('Filter status') }}
                                    </label>
                                    <select
                                        id="translation-status-filter"
                                        v-model="filters.status"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        @change="onFilterChanged"
                                    >
                                        <option value="all">{{ $t('All entries') }}</option>
                                        <option value="missing">{{ $t('Only missing') }}</option>
                                        <option value="translated">{{ $t('Only translated') }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-xs font-medium text-gray-600 mb-1" for="translation-search-filter">
                                        {{ $t('Search') }}
                                    </label>
                                    <input
                                        id="translation-search-filter"
                                        v-model="filters.search"
                                        :placeholder="$t('Search translation key or value')"
                                        class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        type="text"
                                        @input="onSearchChanged"
                                    />
                                </div>
                            </div>
                            <div class="flex flex-col">
                                <div class="-my-2 -mx-6 overflow-x-auto">
                                    <div class="px-2 align-middle inline-block min-w-full">
                                        <table class="max-w-full divide-y divide-gray-200">
                                            <thead>
                                            <tr>
                                                <th class="px-6 py-3 w-3/6 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                                    {{ $t('Source text') }}
                                                </th>
                                                <th class="px-6 py-3 w-1/6 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                                    {{ $t('Scope') }}
                                                </th>
                                                <th class="px-6 py-3 w-3/6 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                                                    {{ $t('Translation') }}
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <template v-for="translate in translations" :key="translate.key">
                                                <tr class="bg-white">
                                                    <td class="px-6 py-4 w-3/6 text-sm leading-5 font-medium text-gray-900">
                                                        {{ translate.key }}
                                                    </td>
                                                    <td class="px-6 py-4 w-1/6 text-sm leading-5 text-gray-900">
                                                        <span
                                                            :class="translate.is_global ? 'bg-amber-100 text-amber-700' : 'border'"
                                                            :style="translate.is_global ? {} : greenBadgeStyle()"
                                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                                        >
                                                            {{ translate.is_global ? $t('Global') : $t('Local') }}
                                                        </span>
                                                        <div class="text-xs text-gray-500 mt-1">
                                                            {{ $t('Used in :count components', {count: translate.usage_count}) }}
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 w-3/6 text-sm leading-5 text-gray-900">
                                                        <textarea
                                                            v-model="translate.value"
                                                            :class="translate.value !== translate.key ? 'translation-edited' : ''"
                                                            :placeholder="$t('Enter the translated string')"
                                                            class="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                            rows="1"
                                                        />
                                                    </td>
                                                </tr>
                                            </template>
                                            <tr v-if="translations.length === 0" class="bg-white">
                                                <td class="px-6 py-4 text-sm leading-5 text-gray-500" colspan="3">
                                                    {{ $t('No translations found for selected filters') }}
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="stage-footer-surface text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded mr-4"
                                to="/workspace/admin/settings/translations"
                            >
                                {{ $t('Close') }}
                            </router-link>
                            <button
                                class="btn btn-gray shadow-sm rounded"
                                type="submit"
                            >
                                {{ $t('Edit language') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
        <confirm-dialog
            :open="deleteLanguageModal"
            :title="$t('Delete language').toString()"
            :text="`${$t('Are you sure you want to delete the language?')} ${$t('All translations will be removed and the application will no longer be available in this language')}. ${$t('This action cannot be undone')}.`"
            :confirm-label="$t('Delete language').toString()"
            confirm-variant="danger"
            @close="deleteLanguageModal = false"
            @confirm="deleteLanguage"
        />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';

export default {
    components: {ConfirmDialog},
    name: "edit-language",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit language')
        }
    },
    data() {
        return {
            loading: true,
            defaultLocale: document.documentElement.lang,
            deleteLanguageModal: false,
            language: {
                name: null,
                locale: null,
            },
            translations: [],
            availableFiles: [],
            filters: {
                file: '',
                search: '',
                status: 'all',
            },
            searchDebounceTimer: null,
        }
    },
    beforeUnmount() {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    },
    mounted() {
        this.getLanguage();
    },
    methods: {
        greenBadgeStyle() {
            return {
                color: 'rgb(255 255 255)',
                backgroundColor: 'rgb(17 165 77 / 75%)',
                borderColor: 'rgb(3 120 50 / 30%)',
            };
        },
        saveLanguage() {
            const self = this;
            self.loading = true;
            axios.put('api/workspace/admin/languages/' + self.$route.params.id, {
                strings: self.translations.map((translation) => ({
                    key: translation.key,
                    value: translation.value,
                }))
            }).then(function () {
                if (self.language.locale === self.defaultLocale) {
                    axios.get('api/lang/' + self.$i18n.locale).then(function (response) {
                        self.$i18n.setLocaleMessage(self.$i18n.locale, response.data);
                    });
                }
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        getLanguage() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/languages/' + self.$route.params.id, {
                params: {
                    file: self.filters.file || undefined,
                    search: self.filters.search || undefined,
                    status: self.filters.status || 'all',
                }
            }).then(function (response) {
                self.language = response.data.language;
                self.translations = response.data.translations;
                self.availableFiles = response.data.files || [];
                self.filters.file = response.data.filters && typeof response.data.filters.file === 'string'
                    ? response.data.filters.file
                    : self.filters.file;
                self.filters.search = response.data.filters && typeof response.data.filters.search === 'string'
                    ? response.data.filters.search
                    : self.filters.search;
                self.filters.status = response.data.filters && typeof response.data.filters.status === 'string'
                    ? response.data.filters.status
                    : self.filters.status;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/admin/settings/translations');
            });
        },
        deleteLanguage() {
            const self = this;
            axios.delete('api/workspace/admin/languages/' + self.$route.params.id).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/workspace/admin/settings/translations');
            }).catch(function () {
                self.deleteLanguageModal = false;
            });
        },
        onFilterChanged() {
            this.getLanguage();
        },
        onSearchChanged() {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.getLanguage();
            }, 250);
        }
    }
}
</script>
